import { ethers } from "ethers";
import { decodeContractError } from "./contractErrors";
import {CHAIN_CONFIG, PENDING_REQUEST_MESSAGE, VERSIONS_LOGIC_ADDRESSES, WALLET_LOGOS,} from "./constants";
import TransportWebUSB from "@ledgerhq/hw-transport-webusb";
import Eth from "@ledgerhq/hw-app-eth";
import {toRaw} from "vue";
import {getPreferredProvider} from "./utils";
import { hasMeaningfulValue } from "./utils/valueChecks";

const revertKeyword = "reverted with reason string '";
const endQuote = "'";
const revertRegex = /reverted with reason string ['"]([^'"]+)['"]/i;

const extractReasonFromText = (text) => {
  if (typeof text !== "string") {
    return null;
  }
  const regexMatch = text.match(revertRegex);
  if (regexMatch && regexMatch[1]) {
    return regexMatch[1];
  }
  const startIndex = text.indexOf(revertKeyword);
  if (startIndex === -1) {
    return null;
  }
  const endIndex = text.indexOf(endQuote, startIndex + revertKeyword.length);
  if (endIndex === -1) {
    return null;
  }
  return text.substring(startIndex + revertKeyword.length, endIndex);
};

const safeStringify = (value) => {
  try {
    if (typeof value === "string") {
      return value;
    }
    return JSON.stringify(value);
  } catch {
    return null;
  }
};

const handleObjectError = (err) => {
  if (!err || typeof err !== "object") {
    return null;
  }
  if (err.code === -32002) {
    return PENDING_REQUEST_MESSAGE;
  }
  if (typeof err.message === "string") {
    return extractReasonFromText(err.message) || err.message;
  }
  const serialized = safeStringify(err);
  if (serialized !== null) {
    return extractReasonFromText(serialized) || serialized;
  }
  return err;
};

export const extractMetamaskErrorMessage = (error) => {
  if (!hasMeaningfulValue(error)) {
    return null;
  }

  const decoded = decodeContractError(error);
  if (decoded) {
    return decoded;
  }

  if (typeof error === "object") {
    return handleObjectError(error);
  }

  if (typeof error === "string") {
    try {
      const parsed = JSON.parse(error);
      const parsedResult = handleObjectError(parsed);
      if (parsedResult !== null) {
        return parsedResult;
      }
    } catch {
      // ignore – fall back to plain string handling
    }
    return extractReasonFromText(error) || error;
  }

  return String(error);
};

export const getBackendURL = () => {
  return process.env.VUE_APP_API_URL;
};

export const createInjectedWeb3Provider = (provider) =>
  new ethers.providers.Web3Provider(toRaw(provider), "any");

export const getURLFromPieces = (...args) => {
  return new URL(args.join("/"), process.env.VUE_APP_API_URL).toString();
};

export const stringToBytes32 = (str) => {
  if (typeof str !== "string") {
    throw new TypeError("str must be a string");
  }
  let bytes = ethers.utils.toUtf8Bytes(str);
  if (bytes.length > 32) {
    bytes = bytes.slice(0, 32);
  }
  let result = new Uint8Array(32);
  result.set(bytes);
  return ethers.utils.hexlify(result);
};

export const getLedgerSignedTransaction = async ({
  contractAddress,
  data,
  provider,
  currentAccountAddress,
  networkId,
  derivationPath,
}) => {
  let transport;
  try {
    if (
      !provider ||
      typeof provider.getTransactionCount !== "function" ||
      typeof provider.getGasPrice !== "function"
    ) {
      throw new Error("Missing provider RPC methods");
    }
    if (typeof derivationPath !== "string" || !derivationPath.trim()) {
      throw new Error("Invalid derivation path");
    }
    const chainId = Number(networkId);
    if (!Number.isInteger(chainId) || chainId <= 0) {
      throw new Error("Invalid network id");
    }
    const toAddress = ethers.utils.getAddress(contractAddress);
    const signerAddress = ethers.utils.getAddress(currentAccountAddress);

    transport = await TransportWebUSB.create();
    const eth = new Eth(transport);
    const gasLimitValue = ethers.utils.parseUnits("1000000", "wei");

    const nonce = await provider.getTransactionCount(signerAddress);
    const rawTx = {
      to: toAddress,
      data: data,
      gasLimit: ethers.utils.hexlify(gasLimitValue),
      gasPrice: await provider.getGasPrice(),
      nonce: nonce,
      chainId,
    };
    let unsignedTx = ethers.utils.serializeTransaction(rawTx).substring(2);
    const signature = await eth.signTransaction(derivationPath, unsignedTx);
    signature.r = "0x" + signature.r;
    signature.s = "0x" + signature.s;
    signature.v = parseInt(signature.v, 16);
    let signedTx = ethers.utils.serializeTransaction(rawTx, signature);

    return { signedTx };
  } catch (error) {
    throw new Error(`Ledger transaction signing failed: ${error.message}`);
  } finally {
    if (transport) {
      await transport.close();
    }
  }
};

export const getProvider = async (
  initProvider,
  adapter,
  currentAccountType,
  networkId,
  preProvider
) => {
  const unwrap = (provider) => (provider ? toRaw(provider) : provider);
  if (preProvider) {
    return unwrap(preProvider);
  }
  if (initProvider) {
    return unwrap(initProvider);
  }
  if (adapter && adapter.provider) {
    return unwrap(adapter.provider);
  }

  const chainConfig = networkId ? CHAIN_CONFIG[networkId] : undefined;
  const hasWindow = typeof window !== "undefined";
  const ethereum = hasWindow ? window.ethereum : undefined;
  const createDefaultProvider = () => {
    if (chainConfig?.rpcEndPoint) {
      return ethers.providers.getDefaultProvider(chainConfig.rpcEndPoint);
    }
    return ethers.providers.getDefaultProvider();
  };

  if (currentAccountType === "Ledger") {
    if (!chainConfig?.rpcEndPoint) {
      throw new Error(`Missing RPC endpoint for network ${networkId}`);
    }
    return createDefaultProvider();
  }

  if (!currentAccountType || !ethereum) {
    return createDefaultProvider();
  }

  if (currentAccountType === "MetaMask") {
    const preferred = await getPreferredProvider();
    return createInjectedWeb3Provider(preferred);
  }

  return createInjectedWeb3Provider(ethereum);
};

export const getPreferredProviderEthers = async (preProvider) => {
  if (preProvider) {
    return toRaw(preProvider);
  }
  return createInjectedWeb3Provider(await getPreferredProvider());
};

export const calculateActionId = (actionName, param, contractAddress) => {
  if (typeof actionName !== "string") {
    throw new TypeError("actionName must be a string");
  }
  const normalizedParam = ethers.BigNumber.from(param);
  if (normalizedParam.lt(0)) {
    throw new RangeError("param must be non-negative");
  }
  const normalizedAddress = ethers.utils.getAddress(contractAddress);
  const encoded = ethers.utils.solidityPack(
    ["string", "uint256", "address"],
    [actionName, normalizedParam, normalizedAddress]
  );
  return ethers.utils.keccak256(encoded);
};
export const updateReactiveArray = (targetArray, sourceArray) => {
  targetArray.splice(0, targetArray.length, ...(sourceArray || []));
};
export const getLogicByVersion = (version) => {
  return VERSIONS_LOGIC_ADDRESSES[version];
};
export function detectWallets() {
  if (typeof window === "undefined") {
    return [];
  }
  const detected = [];
  const seen = new Set();
  const addWallet = (id, name, logo, provider) => {
    if (seen.has(id)) {
      return;
    }
    seen.add(id);
    detected.push({ id, name, logo, provider });
  };
  const safeGet = (provider, key) => {
    try {
      return provider?.[key];
    } catch {
      return undefined;
    }
  };
  const ethereum = window.ethereum;
  const providerList = ethereum
    ? ethereum.providers && Array.isArray(ethereum.providers)
      ? toRaw(ethereum.providers)
      : [ethereum]
    : [];

  providerList.forEach((provider) => {
    if (!provider) {
      return;
    }
    const hasRequestFn = () => {
      try {
        return typeof provider.request === "function";
      } catch {
        return false;
      }
    };
    if (safeGet(provider, "isMetaMask") && !safeGet(provider, "isBraveWallet")) {
      if (hasRequestFn()) {
        addWallet("metamask", "MetaMask", WALLET_LOGOS.metamask, provider);
      }
    }
    if (
      safeGet(provider, "isCoinbaseWallet") ||
      safeGet(provider, "isCoinbaseBrowserExtension")
    ) {
      addWallet("coinbase", "Coinbase Wallet", WALLET_LOGOS.coinbase, provider);
    }
    if (safeGet(provider, "isTrust")) {
      addWallet("trust", "Trust Wallet", WALLET_LOGOS.trust, provider);
    }
    if (safeGet(provider, "isExodus")) {
      addWallet("exodus", "Exodus Wallet", WALLET_LOGOS.exodus, provider);
    }
    if (safeGet(provider, "isArgent")) {
      addWallet("argent", "Argent Wallet", WALLET_LOGOS.argent, provider);
    }
    if (safeGet(provider, "isAtomic")) {
      addWallet("atomic", "Atomic Wallet", WALLET_LOGOS.atomic, provider);
    }
    if (safeGet(provider, "isBraveWallet")) {
      return;
    }
  });

  if (window.okxwallet) {
    addWallet("okx", "OKX Wallet", WALLET_LOGOS.okx, window.okxwallet);
  }

  return detected;
}
